import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../utils/api';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Pagination from '../../components/Pagination';
import './ScormCourses.css';

// Default thumbnail for courses without images
const defaultThumbnail = 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=300&fit=crop';

const ScormCourses = () => {
  const navigate = useNavigate();
  const [allCourses, setAllCourses] = useState([]);
  const [myCourses, setMyCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(9);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [enrollingId, setEnrollingId] = useState(null);

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const [all, my] = await Promise.all([
        apiFetch(`${window._env_.API_BASE_URL}/scorm/courses`),
        apiFetch(`${window._env_.API_BASE_URL}/scorm/my-courses`)
      ]);
      setAllCourses(all || []);
      setMyCourses(my || []);
    } catch (error) {
      console.error('Error fetching SCORM courses:', error);
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async (courseId) => {
    setEnrollingId(courseId);
    try {
      await apiFetch(`${window._env_.API_BASE_URL}/scorm/courses/${courseId}/enroll`, {
        method: 'POST'
      });
      toast.success('Successfully enrolled. You can start learning now.');
      fetchCourses();
    } catch (error) {
      toast.error(error.message || 'Failed to enroll');
    } finally {
      setEnrollingId(null);
    }
  };

  const handleLaunch = (courseId) => {
    navigate(`/scorm/player/${courseId}`);
  };

  const isEnrolled = (courseId) => {
    return myCourses.some((c) => c.scorm_course_id === courseId);
  };

  const getEnrolledCourse = (courseId) => {
    return myCourses.find((c) => c.scorm_course_id === courseId);
  };

  const getStatusBadge = (course) => {
    if (course.completion_status === 'completed') {
      return course.success_status === 'passed'
        ? { text: 'Passed', class: 'passed' }
        : course.success_status === 'failed'
          ? { text: 'Failed', class: 'failed' }
          : { text: 'Completed', class: 'completed' };
    }
    if (course.completion_status === 'incomplete') {
      return { text: 'In Progress', class: 'in-progress' };
    }
    return { text: 'Not Started', class: 'not-started' };
  };

  // Filter courses
  const displayedCourses = activeTab === 'all' ? allCourses : myCourses;
  const filteredCourses = displayedCourses.filter((course) => {
    if (categoryFilter && course.category !== categoryFilter) return false;
    if (levelFilter && course.course_level !== levelFilter) return false;
    return true;
  });

  // Get unique categories and levels for filters
  const categories = [...new Set(allCourses.map((c) => c.category).filter(Boolean))];
  const levels = [...new Set(allCourses.map((c) => c.course_level).filter(Boolean))];

  // Paginate
  const paginatedCourses = filteredCourses.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (loading) {
    return (
      <div className="scorm-courses-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading SCORM Courses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="scorm-courses-page">
      <ToastContainer />

      <div className="scorm-header">
        <div className="header-content">
          <h1>SCORM Learning Center</h1>
          <p>Interactive e-learning courses with progress tracking and assessments</p>
        </div>
      </div>

      <div className="scorm-controls">
        <div className="tabs">
          <button
            className={activeTab === 'all' ? 'active' : ''}
            onClick={() => { setActiveTab('all'); setCurrentPage(1); }}
          >
            All Courses ({allCourses.length})
          </button>
          <button
            className={activeTab === 'my' ? 'active' : ''}
            onClick={() => { setActiveTab('my'); setCurrentPage(1); }}
          >
            My Courses ({myCourses.length})
          </button>
        </div>

        <div className="filters">
          <select
            value={categoryFilter}
            onChange={(e) => { setCategoryFilter(e.target.value); setCurrentPage(1); }}
          >
            <option value="">All Categories</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          <select
            value={levelFilter}
            onChange={(e) => { setLevelFilter(e.target.value); setCurrentPage(1); }}
          >
            <option value="">All Levels</option>
            {levels.map((level) => (
              <option key={level} value={level}>{level}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="courses-grid">
        {paginatedCourses.length === 0 ? (
          <div className="no-courses">
            <span className="icon">📚</span>
            <h3>No courses found</h3>
            <p>{activeTab === 'my' ? 'You haven\'t enrolled in any courses yet.' : 'No courses match your filters.'}</p>
          </div>
        ) : (
          paginatedCourses.map((course) => {
            const enrolled = isEnrolled(course.scorm_course_id);
            const enrolledData = enrolled ? getEnrolledCourse(course.scorm_course_id) : null;
            const status = enrolledData ? getStatusBadge(enrolledData) : null;

            return (
              <div className="course-card" key={course.scorm_course_id}>
                <div className="card-image">
                  <img
                    src={course.thumbnail_url ? `${window._env_.API_BASE_URL}/scorm/thumbnail/${course.thumbnail_url}` : defaultThumbnail}
                    alt={course.title}
                    onError={(e) => { e.target.src = defaultThumbnail; }}
                  />
                  <span className={`scorm-badge scorm-${course.scorm_version.replace('.', '')}`}>
                    SCORM {course.scorm_version}
                  </span>
                  {status && (
                    <span className={`status-badge ${status.class}`}>{status.text}</span>
                  )}
                </div>

                <div className="card-content">
                  <div className="card-meta">
                    {course.category && <span className="category">{course.category}</span>}
                    {course.course_level && <span className="level">{course.course_level}</span>}
                  </div>

                  <h3 className="card-title">{course.title}</h3>

                  {course.description && (
                    <p className="card-description">
                      {course.description.length > 100
                        ? course.description.substring(0, 100) + '...'
                        : course.description}
                    </p>
                  )}

                  <div className="card-info">
                    {course.duration && (
                      <span className="info-item">
                        <span className="icon">⏱</span> {course.duration}
                      </span>
                    )}
                    <span className="info-item">
                      <span className="icon">🎯</span> Pass: {course.passing_score}%
                    </span>
                  </div>

                  {enrolled && enrolledData && enrolledData.progress_measure !== undefined && (
                    <div className="progress-section">
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{ width: `${(enrolledData.progress_measure || 0) * 100}%` }}
                        ></div>
                      </div>
                      <span className="progress-text">
                        {Math.round((enrolledData.progress_measure || 0) * 100)}% complete
                      </span>
                      {enrolledData.score_raw !== null && enrolledData.score_raw !== undefined && (
                        <span className="score-text">Score: {enrolledData.score_raw}%</span>
                      )}
                    </div>
                  )}

                  <div className="card-actions">
                    {enrolled ? (
                      <button
                        className="launch-btn"
                        onClick={() => handleLaunch(course.scorm_course_id)}
                      >
                        {enrolledData?.completion_status === 'completed' ? 'Review Course' : 'Continue Learning'}
                      </button>
                    ) : (
                      <button
                        className="enroll-btn"
                        onClick={() => handleEnroll(course.scorm_course_id)}
                        disabled={enrollingId === course.scorm_course_id}
                      >
                        {enrollingId === course.scorm_course_id ? 'Enrolling...' : 'Enroll Now'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {paginatedCourses.length > 0 && (
        <Pagination
          items={filteredCourses}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        />
      )}
    </div>
  );
};

export default ScormCourses;



